/**
 * @fileoverview Static adapter for {@link AppCatalogPort} — the built-in
 * catalog, behind the interface.
 *
 * The list itself has not moved: `components/application-portal/app-catalog.ts`
 * is still where an app is added, and still the only source of truth for
 * readiness. What changed is that the SCREEN no longer reads it. This adapter
 * is the one place that knows the catalog is a hard-coded array, so replacing
 * it with an API-backed or per-agency-gated one is a change here and nowhere
 * else.
 *
 * Categories carry their own counts, computed once, because the port says the
 * count is the catalog's to report rather than the screen's to derive — a
 * catalog that gates rows per agency would still show the full figure.
 */

import {
  IRONCASE_APP_CATALOG,
  IRONCASE_APP_CATEGORIES,
} from "../../components/application-portal/app-catalog.js";
import type { AppCatalog, AppCatalogPort, CatalogApp } from "../ports/app-catalog.js";

/**
 * The server-injected route map, read the way Foundry's shell reads its own.
 *
 * `WorkspaceBootstrapMetaAdapter.readWorkspaceBootstrapState()` pulls
 * `applicationRoutes` and `disabledApplicationRoutes` straight out of
 * `document` (../palantir, recovered from droneconnection.usw-23), and THROWS
 * when a required meta is absent rather than booting without it. This does the
 * same: a launcher rendered from a silently-empty map is a product that appears
 * to contain nothing, and the cause would be nowhere near the symptom.
 *
 * Returns null only when there is no document at all — a unit test importing
 * this module has no head to read, and should get the built-in list rather than
 * a thrown error about a browser it is not in.
 */
function readInjectedRoutes(): { routes: Record<string, string>; disabled: Set<string> } | null {
  if (typeof document === "undefined") return null;
  const read = (name: string) => {
    const el = document.querySelector(`meta[name="${name}"]`);
    const content = el?.getAttribute("content");
    if (content == null) {
      throw new Error(
        `IronCase bootstrap is missing ${name} — index.html was served without ` +
          `its injected application routes (see vite.config.ts).`,
      );
    }
    return JSON.parse(content) as unknown;
  };
  const environment = read("x-workspace-page-environment-data") as {
    applicationRoutes: Record<string, string>;
    disabledApplicationRoutes: string[];
  };
  const routes = environment.applicationRoutes;
  const disabled = environment.disabledApplicationRoutes;
  if (!routes || !Array.isArray(disabled)) {
    throw new Error("IronCase workspace bootstrap has invalid application route fields");
  }
  return { routes, disabled: new Set(disabled) };
}

export class StaticAppCatalogAdapter implements AppCatalogPort {
  load(): AppCatalog {
    /*
      The injected map is the AUTHORITY on which apps exist and which are
      reachable; the module supplies the presentation each one needs (title,
      blurb, icon, colour) that a route map does not carry. Foundry splits it the
      same way — `applicationRoutes` is ids to URLs, and the launcher's labels
      come from the app manifests.
    */
    const injected = readInjectedRoutes();
    const apps: CatalogApp[] = IRONCASE_APP_CATALOG.map((e) => ({
      id: e.id,
      title: e.name,
      description: e.description,
      icon: e.icon,
      color: e.color,
      category: e.category,
      // An unbuilt app has no destination. The screen decides how to present
      // that; it should not have to infer it from a placeholder href.
      // An app is reachable when the injected map has a route for it and has
      // not disabled it. Falling back to the module's own status keeps tests
      // and dev-server runs working without a build step.
      href: (injected ? injected.disabled.has(e.href) : e.status === "coming-soon")
        ? undefined
        : (injected?.routes[e.id] ?? e.href),
      status: (injected ? injected.disabled.has(e.href) : e.status === "coming-soon")
        ? "coming-soon"
        : "live",
    }));

    const counts = new Map<string, number>();
    for (const c of IRONCASE_APP_CATEGORIES) counts.set(c, 0);
    for (const a of apps) counts.set(a.category, (counts.get(a.category) ?? 0) + 1);

    return {
      categories: IRONCASE_APP_CATEGORIES.map((label) => ({
        id: label,
        label,
        count: counts.get(label) ?? 0,
      })),
      apps,
      total: apps.length,
    };
  }
}
